import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  BRAND_ALTERNATE_NAMES,
  BRAND_DESCRIPTION,
  BRAND_FULL_NAME,
  BRAND_KEYWORDS,
  BRAND_NAME,
  BRAND_THEME_COLOR,
  BRAND_TITLE,
  CONTACT_EMAIL,
  CONTACT_FACEBOOK_URL,
  CONTACT_WHATSAPP,
  CONTACT_WHATSAPP_URL,
  CONTACT_YOUTUBE_URL,
  SITE_URL,
  clampSeoDescription,
  clampSeoTitle,
} from '../../config/brand';

export type SeoBreadcrumb = { name: string; path: string };

export interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile' | 'product';
  author?: string;
  /** When true, tells Google not to index this page */
  noIndex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  breadcrumbs?: SeoBreadcrumb[];
  /** Extra schema.org JSON-LD objects (Course, ItemList, etc.) */
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
  /** Emit Organization + WebSite JSON-LD (home only by default) */
  includeSiteWideSchema?: boolean;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = BRAND_TITLE,
  description = BRAND_DESCRIPTION,
  keywords = BRAND_KEYWORDS,
  image = '/logo.png',
  url = `${SITE_URL}/`,
  type = 'website',
  author = BRAND_FULL_NAME,
  noIndex = false,
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  breadcrumbs,
  structuredData,
  includeSiteWideSchema,
}) => {
  const rawTitle = title.includes(BRAND_NAME) ? title : `${title} | ${BRAND_NAME}`;
  const pageTitle = clampSeoTitle(rawTitle, 60);
  const metaDescription = clampSeoDescription(description, 158);

  // Prefer a clean self-canonical: no query string, no hash, trailing slash only on home.
  const normalizedPath = (() => {
    const raw = url.startsWith('http')
      ? url.replace(SITE_URL, '')
      : url.startsWith('/')
        ? url
        : `/${url}`;
    const withoutQuery = raw.split('?')[0].split('#')[0] || '/';
    if (withoutQuery === '/' || withoutQuery === '') return '/';
    return withoutQuery.replace(/\/+$/, '');
  })();
  const fullUrl = `${SITE_URL}${normalizedPath === '/' ? '/' : normalizedPath}`;
  const fullImageUrl = image.startsWith('http')
    ? image
    : `${SITE_URL}${image.startsWith('/') ? image : `/${image}`}`;
  const robots = noIndex
    ? 'noindex, nofollow'
    : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';

  const isHome = normalizedPath === '/';
  const emitSiteSchema = includeSiteWideSchema ?? isHome;

  const crumbList =
    breadcrumbs && breadcrumbs.length > 0
      ? [{ name: BRAND_NAME, path: '/' }, ...breadcrumbs]
      : [{ name: BRAND_NAME, path: '/' }];

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbList.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.path.startsWith('http')
        ? item.path
        : `${SITE_URL}${item.path === '/' ? '/' : item.path}`,
    })),
  };

  const extraJsonLd = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Helmet htmlAttributes={{ lang: 'ar', dir: 'rtl' }}>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="creator" content={BRAND_FULL_NAME} />
      <meta name="publisher" content={BRAND_FULL_NAME} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="bingbot" content={robots} />

      {/* Canonical + hreflang (Arabic-only site) */}
      <link rel="canonical" href={fullUrl} />
      <link rel="alternate" hrefLang="ar" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={pageTitle} />
      <meta property="og:site_name" content={BRAND_TITLE} />
      <meta property="og:locale" content="ar_EG" />
      <meta property="article:author" content={BRAND_FULL_NAME} />

      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {section && <meta property="article:section" content={section} />}
      {tags.length > 0 &&
        tags.map((tag, index) => <meta key={index} property="article:tag" content={tag} />)}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={fullImageUrl} />

      {/* Additional SEO Meta Tags */}
      <meta name="language" content="Arabic" />
      <meta httpEquiv="content-language" content="ar" />
      <meta name="geo.region" content="EG" />
      <meta name="geo.placename" content="Egypt" />
      <meta name="geo.position" content="30.0444;31.2357" />
      <meta name="ICBM" content="30.0444, 31.2357" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="revisit-after" content="1 days" />
      <meta
        name="subject"
        content={`${BRAND_NAME}, ${BRAND_FULL_NAME}, تعليم أونلاين, كل المراحل الدراسية`}
      />
      <meta name="category" content="تعليم أونلاين" />
      <meta name="classification" content="منصة تعليمية" />
      <meta name="coverage" content="Egypt" />
      <meta name="target" content="طلاب كل المراحل الدراسية وأولياء الأمور" />
      <meta name="theme-color" content={BRAND_THEME_COLOR} />
      <meta name="HandheldFriendly" content="true" />
      <meta name="MobileOptimized" content="width" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={BRAND_NAME} />
      <meta name="application-name" content={BRAND_FULL_NAME} />
      <meta name="msapplication-TileColor" content={BRAND_THEME_COLOR} />

      {emitSiteSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'EducationalOrganization',
            '@id': `${SITE_URL}/#organization`,
            name: BRAND_FULL_NAME,
            alternateName: [...BRAND_ALTERNATE_NAMES],
            description: BRAND_DESCRIPTION,
            url: `${SITE_URL}/`,
            image: fullImageUrl,
            logo: {
              '@type': 'ImageObject',
              url: `${SITE_URL}/logo.png`,
            },
            brand: {
              '@type': 'Brand',
              name: BRAND_NAME,
              alternateName: BRAND_FULL_NAME,
              logo: `${SITE_URL}/logo.png`,
            },
            sameAs: [CONTACT_FACEBOOK_URL, CONTACT_YOUTUBE_URL, CONTACT_WHATSAPP_URL].filter(
              Boolean
            ),
            educationalCredentialAwarded: 'شهادة إتمام الدورة',
            knowsAbout: [
              'أوائل مصر',
              'منصة أوائل مصر',
              'تعليم أونلاين',
              'كل المراحل الدراسية',
              'ابتدائي',
              'إعدادي',
              'ثانوي',
              'اللغة العربية',
              'رياضيات',
              'علوم',
              'لغة إنجليزية',
            ],
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'EG',
              addressRegion: 'القاهرة',
            },
            telephone: `+${CONTACT_WHATSAPP}`,
            email: CONTACT_EMAIL,
            areaServed: {
              '@type': 'Country',
              name: 'Egypt',
            },
          })}
        </script>
      )}

      {emitSiteSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            '@id': `${SITE_URL}/#website`,
            name: BRAND_TITLE,
            alternateName: [...BRAND_ALTERNATE_NAMES],
            url: `${SITE_URL}/`,
            description: BRAND_DESCRIPTION,
            inLanguage: 'ar',
            publisher: { '@id': `${SITE_URL}/#organization` },
          })}
        </script>
      )}

      {/* Structured Data for WebPage */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': `${fullUrl}#webpage`,
          name: pageTitle,
          description: metaDescription,
          url: fullUrl,
          inLanguage: 'ar',
          isPartOf: { '@id': `${SITE_URL}/#website` },
          about: [
            { '@type': 'Thing', name: BRAND_NAME },
            { '@type': 'Thing', name: BRAND_FULL_NAME },
            { '@type': 'Thing', name: 'تعليم أونلاين' },
          ],
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: fullImageUrl,
          },
        })}
      </script>

      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      {extraJsonLd.map((data, index) => (
        <script key={`seo-jsonld-${index}`} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
